import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { DataGridColumn } from './data-grid-column';
import { DataGridConfig } from './data-grid-config';
import { ListRequest } from '../../dtos/list-request';
import { SortDirection } from '../../dtos/sort-direction';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-data-grid',
  templateUrl: './data-grid.component.html',
  styleUrl: './data-grid.component.scss',
  standalone: false,
})
export class DataGridComponent implements OnInit, OnChanges {
  @Input() data: any[] = [];
  @Input() columns: DataGridColumn[] = [];
  @Input() config!: DataGridConfig;
  @Input() request!: ListRequest;
  @Input() loading = false;

  @Output() add = new EventEmitter<void>();
  @Output() export = new EventEmitter<void>();
  @Output() commandClick = new EventEmitter<{ row: any; command: string }>();
  @Output() requestChange = new EventEmitter<ListRequest>();

  @ViewChild('dt') dt!: Table;

  globalFilter: string = '';
  selectedColumns: DataGridColumn[] = [];
  visibleColumns: DataGridColumn[] = [];

  ngOnInit(): void {
    if (this.request) {
      this.globalFilter = this.request.searchText ?? '';
      //this.emitRequest(this.request);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['columns']) {
      this.selectedColumns = this.columns.filter(c => c.isVisible);
      this.updateVisibleColumns();
    }
  }

  // =========================
  // CLEAR FILTERS
  // =========================
  clearFilters() {
    this.globalFilter = '';
    const request: ListRequest = {
      pageNumber: 1,
      pageSize: this.request.pageSize,
      searchText: '',
      filters: [],
    };
    this.emitRequest(request);
  }

  // =========================
  // SEARCH
  // =========================
  onSearch(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.globalFilter = value;

    const request: ListRequest = {
      pageNumber: 1,
      pageSize: this.request.pageSize,
      searchText: value,
      filters: this.request.filters,
      sortBy: this.request.sortBy,
      sortOrder: this.request.sortOrder,
    };
    this.emitRequest(request);
  }

  // =========================
  // PAGINATION
  // =========================
  onPage(event: any) {
    const request: ListRequest = {
      pageNumber: event.first / event.rows + 1,
      pageSize: event.rows,
      searchText: this.globalFilter ?? '',
      filters: this.request.filters,
      sortBy: this.request.sortBy,
      sortOrder: this.request.sortOrder,
    };
    this.emitRequest(request);
  }

  // =========================
  // SORTING
  // =========================
  onSort(event: any) {
    const request: ListRequest = {
      pageNumber: 1,
      pageSize: this.request.pageSize,
      sortBy: event.field,
      sortOrder: event.order === 1 ? SortDirection.ASCENDING : SortDirection.DESCENDING,
      searchText: this.globalFilter ?? '',
      filters: this.request.filters,
    };
    this.emitRequest(request);
  }

  // =========================
  // FILTERING
  // =========================
  onFilter(event: any) {
    const request: ListRequest = {
      pageNumber: 1,
      pageSize: this.request.pageSize,
      searchText: this.globalFilter ?? '',
      filters: this.mapFilters(this.dt.filters),
    };
    this.emitRequest(request);
  }

  private mapFilters(filters: any): any[] {
    if (!filters) return [];
    return Object.keys(filters).map(key => ({
      field: key,
      value: filters[key]?.value,
      matchMode: filters[key]?.matchMode,
    }));
  }

  // =========================
  // COLUMN VISIBILITY
  // =========================
  onColumnSelectionChange() {
    this.updateVisibleColumns();
  }

  private updateVisibleColumns() {
    const selectedFields = new Set(this.selectedColumns.map(c => c.field));
    this.visibleColumns = this.columns.filter(c => selectedFields.has(c.field));
  }

  // =========================
  // EMIT REQUEST
  // =========================
  private emitRequest(request: ListRequest) {
    this.request = request;
    this.requestChange.emit(request);
  }

  // =========================
  // COMMANDS
  // =========================
  onGridCommandClick(row: any, command: string) {
    this.commandClick.emit({ row, command });
  }

  onAdd() {
    this.add.emit();
  }

  onExport() {
    const datePipe = new DatePipe('en-US');

    // Define columns to export based on column config and visibility (ignores 'actions' and 'serial' columns)
    const columnsToExport = this.columns
      .filter(col => col.isExportable)
      .map(col => ({
        field: col.field,
        title: col.title,
        type: col.type,
        isLabelConditional: col.isLabelConditional,
        getLabel: col.getLabel,
      }));

    // Map data to include only selected columns and format dates
    const exportData = this.data.map(row => {
      const filteredRow: any = {};
      columnsToExport.forEach(col => {
        let value = row[col.field];
        if (col.type === 'date' && value) {
          value = datePipe.transform(value, 'dd.MM.yyyy HH:mm');
        } else if (col.isLabelConditional) {
          value = col.getLabel ? col.getLabel(row) : value;
        }
        filteredRow[col.title!] = value;
      });
      return filteredRow;
    });

    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);

    // AUTO-FIT columns: calculate max width in chars per column and set width
    const colWidths = columnsToExport.map(col => {
      const maxLength = Math.max(
        col!.title!.length,
        ...exportData.map(row => (row[col.title!] ? row[col.title!].toString().length : 0))
      );
      return { wch: maxLength + 2 }; // +2 for padding
    });
    ws['!cols'] = colWidths;

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, this.config.title || 'Sheet1');

    const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `${this.config.title || 'Sheet1'}_${datePipe.transform(new Date(), 'dd.MM.yyyy HH:mm')}.xlsx`);
  }
}
